'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale } from 'next-intl';
import { type CSSProperties, useState } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { UserAvatar } from '@/components/images/user-avatar';
import { AnimatedOutcomePercent } from '@/components/markets/animated-outcome-percent';
import { type LiveTrade, LiveTradesOverlay } from '@/components/markets/live-trades/live-trades-overlay';
import { UserIdentity } from '@/components/profile/user-identity';
import { Icon } from '@/components/ui/icon';
import { ArrowUp01IconSolid } from '@/components/ui/icon/glyphs';
import { Panel } from '@/components/ui/panel';
import { FEATURED_HERO_CARD_CLICKED } from '@/events/featured-hero';
import type { OutcomeColorClasses } from '@/hooks/chart/use-outcome-color';
import { useIntentRoutePrefetch } from '@/hooks/use-intent-route-prefetch';
import { Link } from '@/i18n/navigation';
import { trackEvent } from '@/lib/analytics';
import { type OutcomeHueName, outcomeHueToStepVar } from '@/lib/chart/outcome-hue';
import { useFormattedCurrency } from '@/lib/currency';
import { formatCount } from '@/lib/format/format-count';
import { cn } from '@/lib/ui/utils';
import type { HeroOutcomeThesis } from './hero-outcome-theses';
import { DEFAULT_OUTCOME_DECOR, type OutcomeDecorParams } from './outcome-decor';
import { OutcomeSparkline, type OutcomeSparklinePoint } from './outcome-sparkline';

// `formatMcap` mirrors market-row-card.tsx — keep identical. The mcap row's
// space is ALWAYS reserved (fixed height) so revealing it on hover only fades
// the value in: the card height never changes. `MCAP_ROW_HEIGHT_PX` matches the
// caption line (14px) plus its 4px top padding.
const MCAP_ROW_HEIGHT_PX = 18;

// Positioned/scaled/rounded/blurred + edge-feathered image box. Per-theme
// opacity is exposed as CSS custom props (`--decor-img-*`) and applied via
// `opacity-[var(--decor-img-l)] dark:opacity-[var(--decor-img-d)]` classes —
// inline `opacity` would beat the `dark:` class on specificity. Mask stops are
// luminance/alpha keywords (black = visible, transparent = hidden).
function decorImageStyle(d: OutcomeDecorParams): CSSProperties {
  const solid = Math.max(0, 100 - d.edgeFade);
  const mask = `radial-gradient(circle closest-side, black ${String(solid)}%, transparent 100%)`;
  return {
    position: 'absolute',
    left: `${String(d.imgX)}%`,
    top: `${String(d.imgY)}%`,
    width: `${String(d.imgSize)}%`,
    aspectRatio: '1',
    transform: 'translate(-50%, -50%)',
    borderRadius: `${String(d.imgRadius)}px`,
    overflow: 'hidden',
    filter: `blur(${String(d.blur)}px)`,
    maskImage: mask,
    WebkitMaskImage: mask,
    '--decor-img-l': String(d.opLight / 100),
    '--decor-img-d': String(d.opDark / 100)
  } as CSSProperties;
}
function decorTintStyle(d: OutcomeDecorParams, hue: OutcomeHueName): CSSProperties {
  return {
    backgroundImage: `radial-gradient(ellipse ${String(d.tintSpread)}% ${String(d.tintSpread)}% at ${String(d.tintFx)}% ${String(d.tintFy)}%, ${outcomeHueToStepVar(hue, 500)} 0%, transparent ${String(d.tintTransparent)}%)`,
    '--decor-tint-l': String(d.tintOpLight / 100),
    '--decor-tint-d': String(d.tintOpDark / 100)
  } as CSSProperties;
}
export interface OutcomeSparklineCardProps {
  marketPubkey: string;
  outcomeIndex: number;
  outcomeLabel: string;
  share: number;
  /** Effective market cap (D6-encoded string) — drives the hover "X Mcap" row. */
  effectiveMarketCapD6: string;
  colorClasses: OutcomeColorClasses;
  /** Decorative outcome image URL (spec 062) — painted faded + blurred in the top-right corner. */
  imageUrl: string | null;
  /** NSFW flag for `imageUrl` (spec 071) — when true the decorative image is not painted. */
  imageNsfw: boolean;
  data: ReadonlyArray<OutcomeSparklinePoint> | undefined;
  /** Latest post-mount realtime trade for this outcome, or null when none exists. */
  degenTriggerId: string | null;
  /** Viewport paired atomically with `data` during timeframe transitions. */
  timeWindowSecs?: number;
  thesis: HeroOutcomeThesis | undefined;
  decimals: boolean;
  slot: number;
  /** Card's live-trade tape — mapped once by the grid, shared by every card. */
  trades: readonly LiveTrade[];
  /** SDK-projected post-mount live trade ids (see `LiveTradesOverlay`). */
  liveSinceMountIds: readonly string[];
  /**
   * Notifies the slot owner (`SparklineCardLeaf`) when the pointer enters /
   * leaves the card, so it can expand this card's chart query to the "all"
   * timeframe on hover. Fires in lockstep with the local `isHovered` reveal.
   */
  onHoverChange?: (hovered: boolean) => void;
  /** Reports completion of the canvas-owned viewport transition. */
  onWindowTransitionComplete?: (windowSecs: number) => void;
  /**
   * Workspace-only override for the decorative image params. Production callers
   * leave this undefined and get {@link DEFAULT_OUTCOME_DECOR}; the
   * `/workspace/outcome-image-tuner` route passes live values so it renders
   * THIS component 1:1 while tuning.
   */
  decorOverride?: OutcomeDecorParams;
  className?: string;
}
export function OutcomeSparklineCard(props) {
  const $ = _c(108);
  const {
    marketPubkey,
    outcomeIndex,
    outcomeLabel,
    share,
    effectiveMarketCapD6,
    colorClasses,
    imageUrl,
    imageNsfw,
    data,
    degenTriggerId,
    timeWindowSecs,
    thesis,
    decimals,
    slot,
    trades,
    liveSinceMountIds,
    onHoverChange,
    onWindowTransitionComplete,
    decorOverride,
    className
  } = props;
  const [isHovered, setIsHovered] = useState(false);
  const locale = useLocale();
  const {
    formatD6
  } = useFormattedCurrency();
  let t0;
  if ($[0] !== effectiveMarketCapD6 || $[1] !== formatD6) {
    t0 = formatD6(effectiveMarketCapD6, "compactDecimal");
    $[0] = effectiveMarketCapD6;
    $[1] = formatD6;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const formattedMcap = `${t0} Mcap`;
  const sharePct = share * 100;
  let t1;
  if ($[3] !== colorClasses.hue) {
    t1 = outcomeHueToStepVar(colorClasses.hue, 400);
    $[3] = colorClasses.hue;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const glowColor = t1;
  const t2 = `radial-gradient(ellipse 85% 75% at 100% 0%, ${glowColor} 0%, transparent 68%)`;
  let t3;
  if ($[5] !== t2) {
    t3 = {
      backgroundImage: t2
    };
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const glowStyle = t3;
  const showOutcomeImage = imageUrl !== null && imageUrl !== "" && imageNsfw === false;
  const decor = decorOverride ?? DEFAULT_OUTCOME_DECOR;
  const outcomeHref = `/markets/${marketPubkey}?outcome=${encodeURIComponent(outcomeLabel)}`;
  const thesisHref = thesis !== undefined ? `/markets/${marketPubkey}?tab=debate&thesis=${thesis.signature}` : undefined;
  const outcomeIntent = useIntentRoutePrefetch(outcomeHref);
  const thesisIntent = useIntentRoutePrefetch(thesisHref);
  let t4;
  if ($[7] !== marketPubkey || $[8] !== outcomeIndex || $[9] !== outcomeLabel || $[10] !== slot) {
    t4 = function trackCardClick() {
      trackEvent(FEATURED_HERO_CARD_CLICKED.name, {
        marketPubkey,
        outcomeIndex,
        outcomeLabel,
        slot
      });
    };
    $[7] = marketPubkey;
    $[8] = outcomeIndex;
    $[9] = outcomeLabel;
    $[10] = slot;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const trackCardClick = t4;
  let t5;
  let t6;
  if ($[12] !== onHoverChange) {
    t5 = () => {
      setIsHovered(true);
      onHoverChange?.(true);
    };
    t6 = () => {
      setIsHovered(false);
      onHoverChange?.(false);
    };
    $[12] = onHoverChange;
    $[13] = t5;
    $[14] = t6;
  } else {
    t5 = $[13];
    t6 = $[14];
  }
  const t7 = `sparkline-card-${String(outcomeIndex)}`;
  let t8;
  if ($[15] !== className) {
    t8 = cn("relative z-10 flex h-full min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-hidden rounded-panel-row border-solid border-border-secondary [border-width:var(--border-width-hairline)] bg-background-primary p-3 shadow-none dark:border-border-secondary dark:shadow-none cursor-pointer", className);
    $[15] = className;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const t9 = `sparkline-card-glow-${String(outcomeIndex)}`;
  let t10;
  if ($[17] !== glowStyle || $[18] !== t9) {
    t10 = <div aria-hidden="true" data-testid={t9} className="pointer-events-none absolute inset-0 rounded-panel-row opacity-10" style={glowStyle} />;
    $[17] = glowStyle;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== colorClasses.hue || $[21] !== decor || $[22] !== imageNsfw || $[23] !== imageUrl || $[24] !== marketPubkey || $[25] !== outcomeIndex || $[26] !== showOutcomeImage) {
    t11 = showOutcomeImage ? <div aria-hidden="true" data-testid={`sparkline-card-image-${String(outcomeIndex)}`} className="pointer-events-none absolute inset-0 overflow-hidden"><div className="opacity-[var(--decor-img-l)] dark:opacity-[var(--decor-img-d)]" style={decorImageStyle(decor)}><OptimizedImage variant="hero" entity={{
          kind: "outcome",
          marketPubkey,
          outcomeIndex,
          hue: colorClasses.hue,
          imageUrl,
          nsfw: imageNsfw
        }} alt="" preview={false} loadReveal="soft" objectPosition="center" sizes="280px" suppressReveal={true} /></div><div className="absolute inset-0 opacity-[var(--decor-tint-l)] dark:opacity-[var(--decor-tint-d)]" style={decorTintStyle(decor, colorClasses.hue)} /></div> : null;
    $[20] = colorClasses.hue;
    $[21] = decor;
    $[22] = imageNsfw;
    $[23] = imageUrl;
    $[24] = marketPubkey;
    $[25] = outcomeIndex;
    $[26] = showOutcomeImage;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== colorClasses.text) {
    t12 = cn("relative z-10 block max-w-full truncate text-body font-medium transition-colors hover:opacity-80", colorClasses.text);
    $[28] = colorClasses.text;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const t13 = `outcome-label-${String(outcomeIndex)}`;
  let t14;
  if ($[30] !== outcomeHref || $[31] !== outcomeIntent || $[32] !== outcomeLabel || $[33] !== t12 || $[34] !== t13 || $[35] !== trackCardClick) {
    t14 = <Link href={outcomeHref} prefetch={false} {...outcomeIntent} onClick={trackCardClick} className={t12} data-testid={t13}>{outcomeLabel}</Link>;
    $[30] = outcomeHref;
    $[31] = outcomeIntent;
    $[32] = outcomeLabel;
    $[33] = t12;
    $[34] = t13;
    $[35] = trackCardClick;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  const t15 = `sparkline-share-${String(outcomeIndex)}`;
  let t16;
  if ($[37] !== decimals || $[38] !== sharePct || $[39] !== t15) {
    t16 = <AnimatedOutcomePercent value={sharePct} decimals={decimals} className="text-display-sm tabular-nums text-foreground-primary" data-testid={t15} />;
    $[37] = decimals;
    $[38] = sharePct;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      height: MCAP_ROW_HEIGHT_PX
    };
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  const t18 = `sparkline-mcap-slot-${String(outcomeIndex)}`;
  const t19 = isHovered ? "opacity-100" : "opacity-0";
  let t20;
  if ($[42] !== colorClasses.text || $[43] !== t19) {
    t20 = cn("pointer-events-none relative z-10 block pt-1 text-caption leading-[14px] font-medium transition-opacity duration-fast ease-default motion-reduce:transition-none", colorClasses.text, t19);
    $[42] = colorClasses.text;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  const t21 = `sparkline-mcap-${String(outcomeIndex)}`;
  let t22;
  if ($[45] !== formattedMcap || $[46] !== t20 || $[47] !== t21) {
    t22 = <span className={t20} data-testid={t21}>{formattedMcap}</span>;
    $[45] = formattedMcap;
    $[46] = t20;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== t18 || $[50] !== t22) {
    t23 = <div className="overflow-hidden" style={t17} data-testid={t18}>{t22}</div>;
    $[49] = t18;
    $[50] = t22;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== t14 || $[53] !== t16 || $[54] !== t23) {
    t24 = <div className="flex flex-col gap-1">{t14}{t16}{t23}</div>;
    $[52] = t14;
    $[53] = t16;
    $[54] = t23;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== timeWindowSecs) {
    t25 = timeWindowSecs !== undefined ? {
      timeWindowSecs
    } : {};
    $[56] = timeWindowSecs;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== onWindowTransitionComplete) {
    t26 = onWindowTransitionComplete === undefined ? {} : {
      onWindowTransitionComplete
    };
    $[58] = onWindowTransitionComplete;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== colorClasses || $[61] !== data || $[62] !== degenTriggerId || $[63] !== t25 || $[64] !== t26) {
    t27 = <OutcomeSparkline data={data} degenTriggerId={degenTriggerId} colorClasses={colorClasses} {...t25} {...t26} />;
    $[60] = colorClasses;
    $[61] = data;
    $[62] = degenTriggerId;
    $[63] = t25;
    $[64] = t26;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== colorClasses.text || $[67] !== liveSinceMountIds || $[68] !== outcomeIndex || $[69] !== trades) {
    t28 = <LiveTradesOverlay outcomeIndex={outcomeIndex} colorClass={colorClasses.text} trades={trades} liveSinceMountIds={liveSinceMountIds} />;
    $[66] = colorClasses.text;
    $[67] = liveSinceMountIds;
    $[68] = outcomeIndex;
    $[69] = trades;
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  let t29;
  if ($[71] !== t27 || $[72] !== t28) {
    t29 = <div className="relative h-16" data-testid="sparkline-chart-slot">{t27}{t28}</div>;
    $[71] = t27;
    $[72] = t28;
    $[73] = t29;
  } else {
    t29 = $[73];
  }
  let t30;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <div className="min-h-0 flex-1" aria-hidden="true" />;
    $[74] = t30;
  } else {
    t30 = $[74];
  }
  const t31 = `outcome-thesis-slot-${String(outcomeIndex)}`;
  let t32;
  if ($[75] !== outcomeIndex || $[76] !== thesis || $[77] !== thesisHref || $[78] !== thesisIntent) {
    t32 = thesis !== undefined && thesisHref !== undefined ? <Link href={thesisHref} prefetch={false} {...thesisIntent} className="relative z-10 line-clamp-2 text-body leading-[18px] text-foreground-secondary transition-colors hover:text-foreground-primary" data-testid={`outcome-thesis-${String(outcomeIndex)}`}>{thesis.content}</Link> : null;
    $[75] = outcomeIndex;
    $[76] = thesis;
    $[77] = thesisHref;
    $[78] = thesisIntent;
    $[79] = t32;
  } else {
    t32 = $[79];
  }
  let t33;
  if ($[80] !== t31 || $[81] !== t32) {
    t33 = <div className="min-h-9" data-testid={t31}>{t32}</div>;
    $[80] = t31;
    $[81] = t32;
    $[82] = t33;
  } else {
    t33 = $[82];
  }
  const t34 = `outcome-thesis-footer-${String(outcomeIndex)}`;
  let t35;
  if ($[83] !== locale || $[84] !== outcomeIndex || $[85] !== thesis) {
    t35 = thesis !== undefined ? <><div className="flex min-w-0 flex-1 items-center gap-2"><UserAvatar userId={thesis.trader.userId} displayName={thesis.trader.displayName} avatarUrl={thesis.trader.avatarUrl} size="xs" className="size-4 shrink-0" rasterise={thesis.trader.avatarPreferences?.rasterise ?? false} nsfw={thesis.trader.avatarNsfw ?? false} {...thesis.trader.walletAddress !== undefined ? {
          colorSeed: thesis.trader.walletAddress
        } : {}} /><UserIdentity user={thesis.trader} variant="inline" size="sm" showProviderBadges={false} showHandle={false} className="min-w-0 truncate" nameClassName="truncate font-normal" /></div><div className="flex shrink-0 items-center gap-0.5 text-caption text-foreground-secondary tabular-nums" data-testid={`outcome-rating-${String(outcomeIndex)}`}><Icon icon={ArrowUp01IconSolid} className="size-3.5" aria-hidden="true" /><span>{formatCount(thesis.rating, locale)}</span></div></> : null;
    $[83] = locale;
    $[84] = outcomeIndex;
    $[85] = thesis;
    $[86] = t35;
  } else {
    t35 = $[86];
  }
  let t36;
  if ($[87] !== t34 || $[88] !== t35) {
    t36 = <div className="flex h-4 shrink-0 items-center justify-between gap-2 min-w-0 overflow-hidden" data-testid={t34}>{t35}</div>;
    $[87] = t34;
    $[88] = t35;
    $[89] = t36;
  } else {
    t36 = $[89];
  }
  const t37 = `sparkline-card-link-${String(outcomeIndex)}`;
  let t38;
  if ($[90] !== outcomeHref || $[91] !== outcomeIntent || $[92] !== outcomeLabel || $[93] !== t37 || $[94] !== trackCardClick) {
    t38 = <Link href={outcomeHref} prefetch={false} {...outcomeIntent} onClick={trackCardClick} aria-label={outcomeLabel} tabIndex={0} data-testid={t37} className="absolute inset-0 z-0 rounded-panel-row outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-border-primary" />;
    $[90] = outcomeHref;
    $[91] = outcomeIntent;
    $[92] = outcomeLabel;
    $[93] = t37;
    $[94] = trackCardClick;
    $[95] = t38;
  } else {
    t38 = $[95];
  }
  let t39;
  if ($[96] !== t10 || $[97] !== t11 || $[98] !== t24 || $[99] !== t29 || $[100] !== t33 || $[101] !== t36 || $[102] !== t38 || $[103] !== t5 || $[104] !== t6 || $[105] !== t7 || $[106] !== t8) {
    t39 = <Panel surface="solid" elevation="flat" density="compact" onMouseEnter={t5} onMouseLeave={t6} data-testid={t7} className={t8}>{t10}{t11}{t24}{t29}{t30}{t33}{t36}{t38}</Panel>;
    $[96] = t10;
    $[97] = t11;
    $[98] = t24;
    $[99] = t29;
    $[100] = t33;
    $[101] = t36;
    $[102] = t38;
    $[103] = t5;
    $[104] = t6;
    $[105] = t7;
    $[106] = t8;
    $[107] = t39;
  } else {
    t39 = $[107];
  }
  return t39;
}